// Envoi d'un signalement (Task B2, vague 2 — observabilité) : construit le
// numéro, valide avec le même schéma que l'Edge Function (rejet tôt d'un
// signalement malformé), assemble le FormData (donnees + capture optionnelle)
// et appelle `signalements`. Un échec ne perd rien (§6.11) : le texte reste
// dans le composant appelant, seule NOTRE phrase (§6.13) est rendue ici —
// jamais le message technique de la bibliothèque Supabase ou de l'edge.

import type { SupabaseClient } from "@supabase/supabase-js";
import type { DeviceInfo } from "../native/diagnostics";
import { invokeErrorMessage } from "../supabase/invoke-error";
import type { Contexte } from "./contexte";
import type { FilEvent } from "./fil";
import { numeroSignalement } from "./numero";
import { type Gravite, signalementSchema } from "./schema";

// Phrase affichée à l'échec (§6.13/§6.11) — reprise telle quelle par
// `SignalerProbleme` : un seul endroit décide du mot, jamais un texte brut de
// bibliothèque ou de serveur.
export const ENVOI_ECHEC_MESSAGE = "L'envoi a échoué. Ton texte est conservé.";

export interface CaptureImage {
  base64: string;
  mimeType: string;
}

export interface EnvoyerSignalementInput {
  client: Pick<SupabaseClient, "functions">;
  description: string;
  gravite: Gravite;
  contexte: Contexte;
  appareil: DeviceInfo;
  fil: FilEvent[];
  /** Diagnostics de l'enregistreur natif, ou `null` (web, ou échec de lecture). */
  diagnostics: unknown;
  erreur: { message: string; pile: string | null } | null;
  /** `null`/absent = pas de vignette jointe (retirée par la personne, ou capture indisponible). */
  capture?: CaptureImage | null;
}

export type EnvoyerSignalementResult =
  | { ok: true; numero: string; url: string }
  | { ok: false; error: string };

// Décode une image base64 (SANS préfixe `data:` — cf. ScreenshotResult) en
// Blob. Fonction PURE : ni DOM (`fetch(data:…)`) ni FileReader, seulement
// `atob` + une vue binaire — testable telle quelle, hors navigateur réel.
export function base64ToBlob(base64: string, mimeType: string): Blob {
  const binaire = atob(base64);
  const octets = new Uint8Array(binaire.length);
  for (let i = 0; i < binaire.length; i++) {
    octets[i] = binaire.charCodeAt(i);
  }
  return new Blob([octets], { type: mimeType });
}

export async function envoyerSignalement(
  input: EnvoyerSignalementInput,
): Promise<EnvoyerSignalementResult> {
  const numero = numeroSignalement(new Date());

  const candidat = {
    numero,
    description: input.description,
    gravite: input.gravite,
    contexte: input.contexte,
    appareil: input.appareil,
    fil: input.fil,
    diagnostics: input.diagnostics ?? null,
    erreur: input.erreur ?? null,
  };

  const parsed = signalementSchema.safeParse(candidat);
  if (!parsed.success) {
    // Jamais montré : un signalement malformé est un défaut de câblage côté
    // app, pas quelque chose que la personne peut corriger en retapant.
    console.error("[envoyerSignalement] signalement invalide :", parsed.error.flatten());
    return { ok: false, error: ENVOI_ECHEC_MESSAGE };
  }

  const formData = new FormData();
  formData.append("donnees", JSON.stringify(parsed.data));
  if (input.capture) {
    const blob = base64ToBlob(input.capture.base64, input.capture.mimeType);
    formData.append("capture", blob, `${numero}.jpg`);
  }

  try {
    const { data, error } = await input.client.functions.invoke("signalements", {
      body: formData,
    });
    if (error) {
      const message = await invokeErrorMessage("signalements", error, ENVOI_ECHEC_MESSAGE);
      return { ok: false, error: message };
    }
    const reponse = (data ?? {}) as { numero?: unknown; url?: unknown };
    return {
      ok: true,
      numero: typeof reponse.numero === "string" ? reponse.numero : numero,
      url: typeof reponse.url === "string" ? reponse.url : "",
    };
  } catch (err) {
    console.error("[envoyerSignalement] appel réseau échoué :", err);
    return { ok: false, error: ENVOI_ECHEC_MESSAGE };
  }
}
